import { makeStyles, SkeletonItem, tokens } from '@fluentui/react-components';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useSelector, useCommand } from '@tdp';
import { AppDetailsHeader } from '@tdp/common/components/AppDetailsHeader';
import { LoadingStates, makeDocsLink } from '@tdp/common/constants';
import { appDetailsSlice } from '@tdp/modules/apps';

import { AppValidationDataGrid } from '../components/AppValidationDataGrid';
import { AppValidationDataGridHeader } from '../components/AppValidationDataGridHeader';
import { AppValidationEmpty } from '../components/AppValidationEmpty';
import { AppValidationNew } from '../components/AppValidationNew';
import { appValidationSlice } from '../redux/slice';

const useStyles = makeStyles({
  pageContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandBackgroundInverted,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    boxSizing: 'border-box',
    height: '100%',
    width: '100%',
    overflowY: 'auto',
    gap: '1.5rem',
    padding: '18px',
  },
  gridContainer: {
    boxShadow: tokens.shadow2,
    borderRadius: tokens.borderRadiusMedium,
  },
});

export const AppValidationPage: React.FC = () => {
  const classes = useStyles();
  const { appId } = useParams();
  const navigate = useNavigate();

  // Commands
  const resetValidation = useCommand((s) => s.appValidationCommands.reset);
  const setValidationAppId = useCommand(
    (s) => s.appValidationCommands.setValidationAppId
  );
  const getValidationCount = useCommand(
    (s) => s.appValidationCommands.getCount
  );
  const getValidationsList = useCommand(
    (s) => s.appValidationCommands.getValidationsList
  );

  // Selectors
  const app = useSelector((s) =>
    appDetailsSlice.selectors.getAppById(s, appId || '')
  );
  const validationAppId = useSelector((s) =>
    appValidationSlice.selectors.validationAppId(s)
  );
  const validationCount = useSelector((s) =>
    appValidationSlice.selectors.validationCount(s)
  );
  const validationList = useSelector((s) =>
    appValidationSlice.selectors.validationList(s)
  );
  const isLoading = useSelector((s) => appValidationSlice.selectors.loading(s));

  // Component Specific States
  const [isNewValidationRequest, setNewValidationRequest] =
    React.useState(false);

  React.useEffect(() => {
    if (!appId) {
      return;
    }
    setValidationAppId(appId);
    return () => {
      resetValidation();
    };
  }, [appId]);

  React.useEffect(() => {
    if (!validationAppId || validationAppId !== appId) {
      return;
    }
    getValidationCount(validationAppId);
    getValidationsList({ appId: validationAppId });
  }, [validationAppId]);

  // Component Specific Functions

  const onValidationDetail = (id: string) => {
    navigate(id, { relative: 'path' });
  };

  const onNewValidationCreated = () => { };

  const DataGrid = () => {
    return (
      <div className={classes.gridContainer}>
        <AppValidationDataGridHeader
          length={validationCount}
          onNewValidationRequest={() => {
            setNewValidationRequest(true);
          }}
        />
        <AppValidationDataGrid
          validations={validationList.appValidations ?? []}
          onValidationDetail={onValidationDetail}
        />
      </div>
    );
  };

  const Validation = () => {
    return (
      <>
        {isNewValidationRequest ? (
          <AppValidationNew
            img="/images/home/featured-stub.png"
            shortName={app.shortName ?? 'App Name'}
            developer={'Developer Name'}
            shortDescription={app.shortDescription ?? 'Short Description'}
            manifestVersion={app.manifestVersion ?? '1.0.0'}
            appVersion={app.version}
            onValidationCreated={onNewValidationCreated}
          />
        ) : validationList.appValidations === null ? (
          <AppValidationEmpty
            onNew={() => {
              setNewValidationRequest(true);
            }}
          />
        ) : (
          <DataGrid />
        )}
      </>
    );
  };

  const ValidationSkeleton = () => {
    return (
      <>
        <SkeletonItem />
        <SkeletonItem />
        <SkeletonItem />
        <SkeletonItem />
        <SkeletonItem />
      </>
    );
  };

  return (
    <div className={classes.pageContainer}>
      <AppDetailsHeader
        onBackNavigation={() => {
          setNewValidationRequest(false);
        }}
        showBackNavigation={isNewValidationRequest}
        title={
          <FormattedMessage
            defaultMessage="App Validation"
            description={'Title for App Validation Section'}
          />
        }
        description={
          <FormattedMessage
            defaultMessage="Prepare your app for its launch and ensure it meets Microsoft's test cases."
            description={'Description for App Validation Section'}
          />
        }
        link={
          <FormattedMessage
            defaultMessage="Learn more about app validation"
            description={'Link to learn more about App Validation'}
          />
        }
        href={makeDocsLink("microsoftteams/platform/concepts/build-and-test/manage-your-apps-in-developer-portal#publish")}
      />
      {isLoading === LoadingStates.SUCCESS ? (
        <Validation />
      ) : (
        <ValidationSkeleton />
      )}
    </div>
  );
};
